
import React, { useContext, useEffect, useRef, useState } from "react";
import SimpleBar from 'simplebar-react';
import { useLocation, useNavigate } from "react-router-dom";
import { CSSTransition } from 'react-transition-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle } from "@fortawesome/free-regular-svg-icons";
import Accordion from "react-bootstrap/Accordion";
import Nav from "react-bootstrap/Nav";
import { faChartLine, faMagnifyingGlassChart, faSignOutAlt, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import Badge from "react-bootstrap/Badge";
import Image from "react-bootstrap/Image";
import Button from "react-bootstrap/Button";
import Dropdown from 'react-bootstrap/Dropdown';
import { Link } from 'react-router-dom';
import { AppRoutes } from "../../routes";
import DefaultProfilePicture from "../../assets/img/profile-pictures/default.jpg";
import { DashboardMapping } from "../../config/dashboards";
import { langMap } from "./i18n";
import LanguageContext from "../../utils/context/i18n/languageContext";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import SidebarProfileSelector from "../SidebarProfileSelector/SidebarProfileSelector";
import { updateResourceRequest } from "../../utils/request/api";
import { Endpoints } from "../../config/api";
import gifLoading from "../../assets/gifs/Lightcast_LoadingGif_Sailor_Red.gif";
import { ModalPopup } from "@bge/global_solution_workbench_commons";

const Sidebar = ({user, logoutHandler, profileHandler, groupList, loading, hideBar, setHideBar}) => {

  const navigate = useNavigate();
  const lang = useContext(LanguageContext).lang
  const location = useLocation();
  const { pathname } = location;
  const [show, setShow] = useState(false);
  const [extModalShow, setExtModalShow] = useState(false);


  useEffect(() => {
    const handleResize = () => {
      setHideBar(window.innerWidth < 765);
      setExtModalShow(window.innerWidth < 765);
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setHideBar, setExtModalShow]);
  
  const displayName = user.firstName ? user.firstName : user.username;

  let imagePath = DefaultProfilePicture;
  if (user.imgPath)
    imagePath = user.imgPath;

  const isDemo =  user.roles && user.roles.some(r => r.name.includes("_DEMO"));
  

  const onLogout = (e) => {
    e.preventDefault();
    console.log("LOGOUT");
    updateResourceRequest(Endpoints.USER_UPDATE_ON_LOGOUT.replace("{id}", user.id), {profileId: user.lastProfile, lastLanguage: lang});
    logoutHandler();
    navigate(AppRoutes.Signin.path);
  }

  const CollapsableNavItem = (props) => {
    const { eventKey, title, icon, iconCode, items, children = null, autoOpen } = props;
    let activeKey = items.some(i => i.code === pathname.split("/")[2]) ? eventKey : "";

    if(autoOpen){
      activeKey = eventKey;
    }

    return (
      <>  
        {
          title !== "Try our data" ?
          <Accordion as={Nav.Item} defaultActiveKey={activeKey}>
              <Accordion.Item eventKey={eventKey}>
                <Accordion.Button as={Nav.Link} className="d-flex justify-content-between align-items-center">
                  <span className="sidebar-content">
                    <span className="sidebar-icon text-secondary-lc">{ iconCode ? <i style={{minWidth: "22px"}} className={`fa-solid ${iconCode}`}></i> : <FontAwesomeIcon icon={icon} />} </span>
                    <span className="sidebar-text text-dark-lc">{title}</span>
                  </span>
                </Accordion.Button>
                <Accordion.Body className="multi-level">
                  <Nav className="flex-column">
                    {children}
                    {
                      items ? items.map ( i => {
                            return <NavItem title={i.name} external={false} onClick={() => {if(window.innerWidth <= 767) setHideBar(false);}} link={AppRoutes.Dashboards_template.path+i.code} key={i.name}/>
                      })
                      :
                      ""
                    }
                  </Nav>
                </Accordion.Body>
              </Accordion.Item>
          </Accordion> 
          :
          <Accordion as={Nav.Item} activeKey={false} className="LG_button">
            <Accordion.Item>
              <Accordion.Button as={Nav.Link} target="_blank" href={DashboardMapping.private.LG_EU} className="d-flex justify-content-between align-items-center">
                <span className="sidebar-link">
                  <span className="sidebar-icon text-primary-lc"><FontAwesomeIcon icon={icon} /> </span>
                  <span className="sidebar-text text-primary-lc">{title}</span>
                </span>
              </Accordion.Button>
            </Accordion.Item>
          </Accordion>
        }
      </>
    );
  };

  const NavItem = (props) => {
    const { title, link, external, target, icon, image, badgeText, badgeBg = "secondary", badgeColor = "primary" } = props;

    const classNames = badgeText ? "d-flex justify-content-start align-items-center justify-content-between" : "";
    const navItemClassName = link.split("/")[2] === pathname.split("/")[2] ? "active" : "";
    const linkProps = external ? { href: link } : { as: Link, to: link };


    return (
      <Nav.Item className={navItemClassName} onClick={() => setShow(false)}>
        <Nav.Link {...linkProps} target={target} onClick={() => { if(window.innerWidth <= 767) setHideBar(true);}} className={classNames}>
          <span style={{wordWrap: "break-word", whiteSpace: "pre-wrap"}}>
            {icon ? <span className="sidebar-icon"><FontAwesomeIcon icon={icon} /> </span> : null}
            {image ? <Image src={image} width={60} height={60} className="sidebar-icon svg-icon" /> : null}

            <span className="sidebar-text">{title}</span>
          </span>
          {badgeText ? (
            <Badge pill bg={badgeBg} text={badgeColor} className="badge-md notification-count ms-2">{badgeText}</Badge>
          ) : null}
        </Nav.Link>
      </Nav.Item>
    );
  };

  const filteredGroupList = groupList.filter(
    (obj, index, self) =>
      index === self.findIndex(o => o.name === obj.name)
  );
  const groupsCounter = useRef(0);

  if(user.solutions){
    filteredGroupList.forEach((group) => {
        let solutions = [];
        if(group.solutions){
          solutions = user.solutions.filter(s => group.solutions.some(gs => gs.id === s.id));
        }else{
          solutions = [{}];
        }
        
        if(JSON.stringify(solutions) !== JSON.stringify([{}])  && JSON.stringify(solutions) !== JSON.stringify([])){
          groupsCounter.current = groupsCounter.current + 1;
        }
    }
  )}

  return (
    hideBar !== "HIDDEN" &&
    <>
      <ModalPopup
        title={`Screen Size Alert`}
        innerTitle={``}
        textButton={`${extModalShow}`}
        textConfig={
          { 
            top: <FontAwesomeIcon icon={faTriangleExclamation} className="alert-popup-icon" />, 
            bottom: "This tool is optimized for desktop devices and larger screens. Your current screen size may limit the experience. For the best experience, use a larger screen or resize your browser window"
          }
        }
        textButtonConfirm={"Continue"}
        setExternalModalShow={setExtModalShow}
        externalModalShow={extModalShow}
        closeError={() => {}}
        buttonFunction={() => setExtModalShow(false)}
        classNameButton="alert-popup d-none"
      /> 
      <CSSTransition timeout={300} in={show} classNames="sidebar-transition">
          <SimpleBar className={`sidebar d-md-block bg-quintary-lc text-dark-lc ${ hideBar === "ALWAYS OPEN" ? "internal-slide-right" : hideBar === null ? "" :  hideBar ? "internal-slide-to-right" : "internal-slide-to-left"}`}>
            <div className="sidebar-inner">
              <Nav className="flex-column">
                <Dropdown as={Nav.Item} className="m-0 border-0">
                  <Dropdown.Toggle as={Nav.Link} className="border-0 user-button">
                    <div className="media d-flex align-items-center">
                      <Image src={imagePath} className="user-avatar md-avatar rounded-circle" />
                      <div className="truncate-text media-body ms-2 align-items-center d-lg-block">
                        <p className="mb-0 font-small truncate-text fw-bold">{user.hideProfile ? "Sign out" : displayName.charAt(0).toUpperCase() + displayName.slice(1)}</p>
                      </div>
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="dropdown-menu-right text-dark p-0 w-100">
                    <Dropdown.Item className="fw-bold p-0 m-0">
                      { 
                        !isDemo && !user.hideProfile &&
                        <Button as={Link} to={AppRoutes.UserProfile.path} className="text-quaternary-lc w-100 profile-button">
                          <FontAwesomeIcon icon={faUserCircle} className="me-2 text-primary-lc" /> {langMap.profile[lang]}
                        </Button>
                      }
                    </Dropdown.Item>
                    {/* <Dropdown.Item className="fw-bold">
                      <FontAwesomeIcon icon={faUserShield} className="me-2" /> {langMap.support[lang]}
                    </Dropdown.Item> */}
                    <Dropdown.Divider />
                    <Dropdown.Item className="fw-bold p-0 m-0">
                      <Button variant="gray-lc-900" onClick={onLogout} className="text-body-bg w-100">
                        <FontAwesomeIcon icon={faSignOutAlt} className="text-body-bg me-2"/> {langMap.signout[lang]}
                      </Button>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Nav>

              <Nav style={{miHeight: "inherit"}} className="sidebar-content flex-column">
                <div className="my-solutions pt-1 pb-1">
                  <FontAwesomeIcon icon={faChartLine} className="me-2 text-secondary-lc" />
                  My solutions
                </div>
                {
                  loading ?
                  <Image className="loading-solutions-logo" src={gifLoading} />
                  :
                  <div className="solutions-group__list">
                  <div className="sidebar-footer-gradient-top"></div>
                  {
                    user.solutions && [...filteredGroupList.sort((a, b) => a.name.localeCompare(b.name)), {name: "Try our data", icon: faMagnifyingGlassChart}].map((group, i) => {
                      let solutions = [];
                      if(group.solutions){
                        solutions = user.solutions.filter(s => group.solutions.some(gs => gs.id === s.id));
                      }else{
                        solutions = [{}];
                      }
                      
                      solutions.sort((s1, s2) => s1.groupRelativeOrder - s2.groupRelativeOrder);

                      return solutions.length ? <CollapsableNavItem autoOpen={group.name !== "Try ou data" && groupsCounter.current === 1} title={group.name} icon={group.icon} iconCode={group.iconCode} items={solutions} key={group.name} eventKey={group.name}/> : ""

                    })
                  }
                  </div>
                }
              </Nav>            

              <Dropdown.Divider />
              {
                !loading && isDemo &&
                <Button className="mt-5 mb-5 contact-us" onClick={() => window.location = `mailto:${user.email}`}>
                  <p>Get in touch to discuss getting our dashboards</p>
                  <p>{`Contact us at ${user.email}`}</p>
                </Button>
              }
            </div>
          </SimpleBar>
      </CSSTransition>
      <div className={`sidebar-footer-gradient-bottom ${ hideBar === "ALWAYS OPEN" ? "internal-slide-right" : hideBar === null ? "" :  hideBar ? "internal-slide-to-right" : "internal-slide-to-left"}`}></div>
      <div className={`fixed-bottom ${ hideBar === "ALWAYS OPEN" ? "internal-slide-right" : hideBar === null ? "" :  hideBar ? "internal-slide-to-right" : "internal-slide-to-left"}`}>
        {
          user.profiles.length > 1 &&  <SidebarProfileSelector
            user={user}  
            handleChange={profileHandler} 
            className="d-none d-md-block"
          />
        }
        <LanguageSelector
          className="d-md-block"
        />
      </div>
      
    </>
  );
};

export default Sidebar;